import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Chip, CircularProgress, Alert,
  Table, TableBody, TableCell, TableHead, TableRow,
  IconButton, Tooltip, TextField, MenuItem,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';

import * as fileApi from '../../lib/api/file';

/**
 * 이 생산계획에서 올린 파일을 한자리에 모아 보여준다.
 *
 * 예전에는 공정을 하나씩 열어야 그 공정에 붙은 파일이 보였다. 그래서 "이 계획에서
 * 그동안 뭘 올렸더라" 를 보려면 공정을 전부 열어봐야 했다.
 *
 * 제조 단계는 종류가 갈려도 전부 파일에 생산계획 UUID 를 달아둔다.
 * 그래서 새로 저장할 것 없이 그것만으로 모을 수 있다.
 */

/** 파일 종류를 사람이 읽는 말로. 등록할 때 쓰는 코드가 그대로 화면에 나오면 안 읽힌다. */
const TYPE_LABEL = {
  design: '설계',
  material: '원자재',
  production: '생산',
  process: '공정',
  qc: 'QC',
  completion: '생산완료',
  shipment: '출하',
};

const TYPE_COLOR = {
  design: 'info',
  material: 'default',
  production: 'primary',
  process: 'success',
  qc: 'warning',
  completion: 'secondary',
  shipment: 'error',
};

const PlanFilesTabContainer = ({ planId }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [typeFilter, setTypeFilter] = useState('');

  const load = useCallback(async () => {
    if (!planId) {
      setFiles([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fileApi.getFilesByPlan(planId);
      const list = res.data?.list || [];
      // 최근에 올린 것이 위로. 대개 방금 올린 것을 찾는다.
      list.sort((a, b) => String(b.crtDt || '').localeCompare(String(a.crtDt || '')));
      setFiles(list);
    } catch (err) {
      setError(err?.response?.data?.message || '파일 목록을 불러오지 못했습니다.');
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [planId]);

  useEffect(() => { load(); }, [load]);

  const handleDownload = async (file) => {
    try {
      const res = await fileApi.downloadFile(file.fileUuid);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = file.fileNm || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('파일을 내려받지 못했습니다.');
    }
  };

  // 화면에 실제로 있는 종류만 거름 선택지로 내놓는다.
  // 쓰지도 않는 종류가 목록에 있으면 고르고 나서 빈 화면을 보게 된다.
  const presentTypes = [...new Set(files.map((f) => f.fileTyp).filter(Boolean))];
  const shown = typeFilter ? files.filter((f) => f.fileTyp === typeFilter) : files;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          이 생산계획에서 올린 파일입니다. 설계 · 공정 · QC · 출하 등 단계와 상관없이 모아서 보여줍니다.
        </Typography>
        {presentTypes.length > 1 && (
          <TextField
            select size="small" label="종류" value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">전체 ({files.length})</MenuItem>
            {presentTypes.map((t) => (
              <MenuItem key={t} value={t}>
                {TYPE_LABEL[t] || t} ({files.filter((f) => f.fileTyp === t).length})
              </MenuItem>
            ))}
          </TextField>
        )}
        <Tooltip title="새로고침">
          <IconButton size="small" onClick={load}><RefreshIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress size={28} />
        </Box>
      ) : shown.length === 0 ? (
        <Alert severity="info">
          {files.length === 0
            ? '이 생산계획에 올라온 파일이 없습니다. 공정을 완료하거나 출하할 때 올린 파일이 여기에 모입니다.'
            : '고른 종류에 해당하는 파일이 없습니다.'}
        </Alert>
      ) : (
        <Paper variant="outlined">
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 100 }}>종류</TableCell>
                  <TableCell sx={{ width: 110 }}>단계</TableCell>
                  <TableCell>파일명</TableCell>
                  <TableCell sx={{ width: 110 }}>등록자</TableCell>
                  <TableCell sx={{ width: 120 }}>등록일</TableCell>
                  <TableCell sx={{ width: 60 }} align="center">받기</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shown.map((f) => (
                  <TableRow key={f.fileUuid} hover>
                    <TableCell>
                      <Chip
                        size="small" variant="outlined"
                        label={TYPE_LABEL[f.fileTyp] || f.fileTyp || '-'}
                        color={TYPE_COLOR[f.fileTyp] || 'default'}
                      />
                    </TableCell>
                    {/* 공정 파일은 여기에 공정명(납품·교육 등)이 들어 있어 어느 공정 것인지 알 수 있다. */}
                    <TableCell>{f.fileAttrVal3 || '-'}</TableCell>
                    <TableCell sx={{ wordBreak: 'break-all' }}>
                      {f.fileNm || '-'}
                      {f.fileDesc && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {f.fileDesc}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{f.crtNm || '-'}</TableCell>
                    <TableCell>{String(f.crtDt || '').substring(0, 10) || '-'}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="내려받기">
                        <IconButton size="small" onClick={() => handleDownload(f)}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default PlanFilesTabContainer;
